import {
  Box,
  Button,
  ButtonGroup,
  // Chip,
  IconButton,
  // Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import * as React from 'react';
import Swal from 'sweetalert2';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import useTheme from '@mui/material/styles/useTheme';
import withReactContent from 'sweetalert2-react-content';

import { ServiceRequestIcon, SubmissionSuccessIcon } from '../../components';
import { CustomButton } from '../../view/InRoomDining';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';

const DEFAULT_OPTIONS = {
  padding: 12,
  showCancelButton: false,
  showConfirmButton: false
};

const STOP_CLOSING_OPTION = {
  allowEscapeKey: false,
  allowOutsideClick: false
};

export const useSweetAlert = () => {
  const isMobileScreen = useMediaQuery('(max-width:480px)');

  const sweetAlert = React.useMemo(() => {
    const SwalWithDefaultOptions = Swal.mixin({
      ...DEFAULT_OPTIONS,
      width: isMobileScreen ? '90vw' : 400
    });
    return withReactContent(SwalWithDefaultOptions);
  }, [isMobileScreen]);

  return sweetAlert;
};

export const useServiceAlert = (onCloseHandlers: () => void) => {
  const theme = useTheme();
  const sweetAlert = useSweetAlert();

  const html = (message: string) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <SubmissionSuccessIcon
        sx={{ height: 60, width: 60 }}
        customColor={theme.palette.primary.main}
      />
      <Typography
        variant="h6"
        sx={{ mt: 2, fontWeight: 600, textAlign: 'center' }}
      >
        {message}
      </Typography>

      <Button
        variant="text"
        sx={{
          mt: 1,
          flex: 1,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' }
        }}
        onClick={() => {
          onCloseHandlers();
          sweetAlert.close();
        }}
      >
        Go back
      </Button>
    </Box>
  );

  return (title: string) =>
    sweetAlert.fire({ html: html(title), ...STOP_CLOSING_OPTION });
};

export const useQuickServiceAlert = () => {
  const theme = useTheme();
  const sweetAlert = useSweetAlert();
  const [serviceCount, setServiceCount] = React.useState(1);
  const [notes, setNotes] = React.useState<string>();
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const html = (service: any) => {
    const canAddMultiple = Boolean(service.qty_by_guest);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <ServiceRequestIcon
          sx={{ height: 60, width: 60 }}
          customColor={theme.palette.primary.main}
        />
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            fontWeight: 600,
            textAlign: 'center',
            fontSize: theme => theme.spacing(2.25)
          }}
        >
          Make request for {service.task}
        </Typography>
        <TextField
          label="Notes"
          variant="outlined"
          fullWidth
          margin="normal"
          value={notes}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNotes(event.target.value);
          }}
        />

        {canAddMultiple && (
          <ButtonGroup
            size="small"
            variant="contained"
            sx={{
              mt: 2,
              border: 'none',
              color: '#ffffff',
              alignSelf: 'center',
              backgroundColor: 'primary.main'
            }}
          >
            <IconButton
              size="small"
              onClick={() => setServiceCount(value => value + 1)}
              sx={{ color: '#ffffff' }}
            >
              <AddIcon fontSize="inherit" />
            </IconButton>
            <CustomButton
              size="small"
              disableRipple
              disableFocusRipple
              disableTouchRipple
              sx={{ border: 'none !important' }}
            >
              {serviceCount}
            </CustomButton>

            <IconButton
              size="small"
              sx={{ color: '#ffffff' }}
              onClick={() =>
                setServiceCount(value => (value >= 2 ? value - 1 : value))
              }
            >
              <RemoveIcon fontSize="inherit" />
            </IconButton>
          </ButtonGroup>
        )}
        {/* Render the DatePicker component conditionally */}
        <Box sx={{ mr: 20 }}>
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="MMMM d, yyyy h:mm aa"
            placeholderText="schedule the task"
            minDate={new Date()} // Set the minimum date to the current date
          />
        </Box>

        <Box sx={{ mt: 3, width: '100%', display: 'flex' }}>
          <Button
            variant="text"
            sx={{
              flex: 1,
              boxShadow: 'none',
              textTransform: 'capitalize',
              '&:hover': { boxShadow: 'none' }
            }}
            onClick={() => {
              sweetAlert.close();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              ml: 1,
              flex: 1,
              boxShadow: 'none',
              textTransform: 'capitalize',
              '&:hover': { boxShadow: 'none' }
            }}
            onClick={() => {
              sweetAlert.clickConfirm();
            }}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    );
  };

  return (service: any) =>
    sweetAlert.fire({
      html: html(service),
      preConfirm: () => {
        const result = [serviceCount, notes, selectedDate];
        setNotes(undefined);
        setSelectedDate(null);
        return result;
      },
      ...STOP_CLOSING_OPTION
    });
};

export const useFlexiPassAlert = () => {
  const sweetAlert = useSweetAlert();
  // const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const openInNewTab = (url: string): void => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };
  const handleCopy = (message: string) => {
    navigator.clipboard.writeText(message);
    openInNewTab('https://www.flexipass.tech/app');
  };
  const html = (message: string) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <LockOpenIcon
        sx={{ height: 70, width: 70, color: theme.palette.primary.main, mb: 2 }}
      />
      <Stack direction="row" alignItems={'center'}>
        <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
          {message}
        </Typography>

        <IconButton
          sx={{
            ml: 1
          }}
          onClick={() => {
            handleCopy(message);
          }}
        >
          <ContentCopyIcon />
        </IconButton>
      </Stack>
    </Box>
  );

  return (title: string) =>
    sweetAlert.fire({ html: html(title), ...DEFAULT_OPTIONS });
};
